import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AppShell } from '@/acc-ui/components/AppShell';
import { PageHeader, Card, SectionLabel, ErrorState } from '@/acc-ui/primitives/Layout';
import { Button } from '@/acc-ui/primitives/Button';
import { Input } from '@/acc-ui/primitives/Input';
import { toast } from '@/acc-ui/primitives/Toast';
import { notificationsApi } from '@/api/notifications-api';

/**
 * Notification preferences — /settings/notifications.
 *
 * Controls which notifications the user receives via email (in-app is
 * always on). Daily digest is on the preferences surface even though the
 * worker lands in Sprint 11 — preferences are already persisted.
 */

export function NotificationPrefsPage() {
  const qc = useQueryClient();
  const prefsQuery = useQuery({
    queryKey: ['notificationPrefs'],
    queryFn: notificationsApi.getPrefs,
  });

  const [emailCritical, setEmailCritical] = useState(true);
  const [emailWarning, setEmailWarning] = useState(false);
  const [digest, setDigest] = useState(false);
  const [digestTime, setDigestTime] = useState('09:00');

  useEffect(() => {
    if (prefsQuery.data) {
      setEmailCritical(prefsQuery.data.emailOnCritical);
      setEmailWarning(prefsQuery.data.emailOnWarning);
      setDigest(prefsQuery.data.digestEnabled);
      setDigestTime(prefsQuery.data.digestTimeOfDay);
    }
  }, [prefsQuery.data]);

  const save = useMutation({
    mutationFn: () =>
      notificationsApi.updatePrefs({
        emailOnCritical: emailCritical,
        emailOnWarning: emailWarning,
        digestEnabled: digest,
        digestTimeOfDay: digestTime,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notificationPrefs'] });
      toast.success('Notification preferences saved.');
    },
    onError: (e) =>
      toast.error(e instanceof Error ? e.message : 'Could not save preferences.'),
  });

  const d = prefsQuery.data;
  const dirty =
    !!d &&
    (emailCritical !== d.emailOnCritical ||
      emailWarning !== d.emailOnWarning ||
      digest !== d.digestEnabled ||
      digestTime !== d.digestTimeOfDay);

  if (prefsQuery.isError) {
    return (
      <AppShell crumb="Settings" title="Notifications">
        <ErrorState
          description="Could not load preferences."
          action={<Button onClick={() => prefsQuery.refetch()}>Retry</Button>}
        />
      </AppShell>
    );
  }

  return (
    <AppShell crumb="Settings" title="Notifications">
      <PageHeader
        title="Notification preferences"
        subtitle="Choose when you also get an email."
      />

      <Card>
        <div className="flex flex-col gap-5">
          <div>
            <SectionLabel>Email delivery</SectionLabel>
            <div className="mt-3 flex flex-col gap-3">
              <Toggle
                label="Email me for critical alerts"
                description="Validation failures that require immediate attention."
                checked={emailCritical}
                onChange={setEmailCritical}
              />
              <Toggle
                label="Email me for warnings"
                description="Findings that need review but are not urgent."
                checked={emailWarning}
                onChange={setEmailWarning}
              />
            </div>
          </div>

          <hr className="border-glass-hairline" />

          <div>
            <SectionLabel>Daily digest</SectionLabel>
            <div className="mt-3 flex flex-col gap-3">
              <Toggle
                label="Send a daily digest"
                description="One email per day summarizing all activity. Replaces per-event warnings."
                checked={digest}
                onChange={setDigest}
              />
              {digest && (
                <label className="flex items-center gap-3">
                  <span className="text-label text-text-secondary">Send at</span>
                  <Input
                    type="time"
                    value={digestTime}
                    onChange={(e) => setDigestTime(e.target.value)}
                    className="max-w-[8rem]"
                  />
                  <span className="text-micro text-text-muted">IST · 24-hour</span>
                </label>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            <Button
              onClick={() => save.mutate()}
              loading={save.isPending}
              disabled={!dirty}
              className="ml-auto"
            >
              Save preferences
            </Button>
          </div>
        </div>
      </Card>
    </AppShell>
  );
}

function Toggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <span
        className={
          'mt-0.5 w-9 h-5 rounded-full transition-colors relative ' +
          (checked ? 'bg-brand-blue' : 'bg-slate-300')
        }
      >
        <span
          className={
            'absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ' +
            (checked ? 'left-[1.125rem]' : 'left-0.5')
          }
        />
      </span>
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <div className="min-w-0">
        <div className="text-label font-medium text-text-primary">{label}</div>
        {description && (
          <div className="text-caption text-text-tertiary">{description}</div>
        )}
      </div>
    </label>
  );
}
